import { useFindSiteAreas } from '@hooks/catalogs/areas'
import { useFindSiteAssignees } from '@hooks/catalogs/assignees'
import { useFindSiteLocations } from '@hooks/catalogs/locations'
import { useFindOperationPosts } from '@hooks/catalogs/operationPosts'
import { useFindSitePlantSections } from '@hooks/catalogs/plantSections'
import { useFindSiteProjects } from '@hooks/catalogs/projects'
import { useFindSiteResources } from '@hooks/catalogs/resources'
import { useFindSiteSupervisors } from '@hooks/catalogs/supervisors'
import { useSiteFeatures } from '@hooks/siteFeatures'
import { useStatus } from '@hooks/status'

interface IFilterData {
  description: string
  id: number
}

interface IFilterPayload {
  statusIds?: number[]
  areaIds?: number[]
  locationIds?: number[]
  supervisorIds?: number[]
  resourceIds?: number[]
  plantSectionIds?: number[]
  operationPostIds?: number[]
  assigneeIds?: number[]
  projectIds?: number[]
}

interface IFilterModel {
  label: string
  options: IFilterData[]
  selected: (IFilterData | undefined)[]
  payloadOption: keyof IFilterPayload
}

interface IFilters {
  status?: IFilterModel
  areas?: IFilterModel
  locations?: IFilterModel
  supervisors?: IFilterModel
  resources?: IFilterModel
  plantSections?: IFilterModel
  operationPosts?: IFilterModel
  assignees?: IFilterModel
  projects?: IFilterModel
}

export function useFilterModels() {
  const { isMidstreamProcess } = useSiteFeatures()
  const { getStatusList, formatStatus } = useStatus()
  const status = getStatusList()

  const { data: areas } = useFindSiteAreas()
  const { data: locations } = useFindSiteLocations()
  const { data: resources } = useFindSiteResources()
  const { data: supervisors } = useFindSiteSupervisors()
  const { data: plantSections } = useFindSitePlantSections()
  const { data: posts } = useFindOperationPosts()
  const { data: assignees } = useFindSiteAssignees()
  const { data: projects } = useFindSiteProjects()

  const statusList: IFilterData[] = status
    .filter((s) => s.name !== 'undefined')
    .map((s) => {
      return {
        description: formatStatus(s.name),
        code: s.name,
        id: s.code,
      }
    })

  const midstreamFilterModel: IFilters = {
    status: {
      label: 'Status',
      options: statusList,
      selected: [],
      payloadOption: 'statusIds',
    },

    areas: {
      label: 'Areas',
      options: areas ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'areaIds',
    },

    locations: {
      label: 'Locations',
      options: locations ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'locationIds',
    },

    resources: {
      label: 'Resources',
      options: resources ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'resourceIds',
    },

    supervisors: {
      label: 'Supervisors',
      options: supervisors ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'supervisorIds',
    },
  }

  const downstreamFilterModel: IFilters = {
    status: {
      label: 'Status',
      options: statusList,
      selected: [],
      payloadOption: 'statusIds',
    },
    projects: {
      label: 'Projects',
      options: projects ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'projectIds',
    },

    supervisors: {
      label: 'Supervisors',
      options: supervisors ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'supervisorIds',
    },

    plantSections: {
      label: 'Plant Sections',
      options: plantSections ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'plantSectionIds',
    },

    locations: {
      label: 'Locations',
      options: locations ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'locationIds',
    },

    operationPosts: {
      label: 'Posts',
      options: posts ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'operationPostIds',
    },

    resources: {
      label: 'Resources',
      options: resources ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'resourceIds',
    },

    assignees: {
      label: 'Assignees',
      options: assignees ?? ([] as IFilterData[]),
      selected: [],
      payloadOption: 'assigneeIds',
    },
  }

  const filterModel = isMidstreamProcess
    ? midstreamFilterModel
    : downstreamFilterModel

  const getFilterModel = () => filterModel

  return { getFilterModel }
}
